import { apiFetcher } from '@/api/api-fetcher'
import { cleanPayload } from '@/utils/apiUtils'
import { csrfManager } from '@/utils/csrf-manager'
import type {
  ServiceResponse,
  ServiceListResponse,
  CompetitorDetailResponse,
  CompetitorResponse,
  PaginatedResponse,
} from '@/types/api/responses'
import type {
  CreateServiceRequest,
  UpdateServiceRequest,
  CreateCompetitorRequest,
  UpdateCompetitorRequest,
} from '@/types/api/requests'

export async function getService(
  spaceId: string,
  serviceId: string,
  options?: { includeCompetitors?: boolean }
): Promise<ServiceResponse> {
  const params: Record<string, string> = {}
  if (options?.includeCompetitors) {
    params.includeCompetitors = 'true'
  }
  return apiFetcher.get(
    `/spaces/${spaceId}/services/${serviceId}`,
    Object.keys(params).length > 0 ? { params } : undefined
  )
}

export async function getServices(
  spaceId: string,
  params?: {
    page?: number
    limit?: number
    sortField?: 'createdAt' | 'serviceName' | 'analysisCount' | 'pinnedAt'
    sortOrder?: 'asc' | 'desc'
    searchQuery?: string
  }
): Promise<{
  services: ServiceListResponse[]
  total: number
  page: number
  limit: number
  hasMore: boolean
}> {
  return apiFetcher.get(`/spaces/${spaceId}/services`, params ? { params } : undefined)
}

export async function createService(
  spaceId: string,
  data: CreateServiceRequest
): Promise<ServiceResponse> {
  // Explicitly create the body
  const body = {
    serviceName: data.serviceName,
    projectName: data.projectName,
    monitoringDomains: data.monitoringDomains,
    isPrivate: data.isPrivate,
    defaultSearchArea: data.defaultSearchArea,
    aliases: data.aliases,
  }
  const cleanedBody = cleanPayload(body)
  return apiFetcher.post(`/spaces/${spaceId}/services`, cleanedBody)
}
export async function updateService(
  spaceId: string,
  serviceId: string,
  data: UpdateServiceRequest
): Promise<ServiceResponse> {
  const cleanedData = cleanPayload(data)
  return apiFetcher.put(`/spaces/${spaceId}/services/${serviceId}`, cleanedData)
}

export async function deleteService(spaceId: string, serviceId: string): Promise<void> {
  return apiFetcher.delete(`/spaces/${spaceId}/services/${serviceId}`)
}

export async function pinService(spaceId: string, serviceId: string): Promise<ServiceResponse> {
  return apiFetcher.patch(`/spaces/${spaceId}/services/${serviceId}/pin`, {})
}

export async function unpinService(spaceId: string, serviceId: string): Promise<ServiceResponse> {
  return apiFetcher.patch(`/spaces/${spaceId}/services/${serviceId}/unpin`, {})
}

// --- Competitor CRUD Operations ---

// Create a single competitor
export async function createCompetitor(
  spaceId: string,
  serviceId: string,
  data: CreateCompetitorRequest
): Promise<CompetitorDetailResponse> {
  // Assuming the API returns the created competitor details
  return apiFetcher.post(`/spaces/${spaceId}/services/${serviceId}/competitors`, data)
}

// Update a single competitor
export async function updateCompetitor(
  spaceId: string,
  serviceId: string,
  competitorId: string,
  data: UpdateCompetitorRequest
): Promise<CompetitorDetailResponse> {
  // Assuming the API returns the updated competitor details
  return apiFetcher.patch(
    `/spaces/${spaceId}/services/${serviceId}/competitors/${competitorId}`,
    data
  )
}

// Delete a single competitor
export async function deleteCompetitor(
  spaceId: string,
  serviceId: string,
  competitorId: string
): Promise<void> {
  return apiFetcher.delete(`/spaces/${spaceId}/services/${serviceId}/competitors/${competitorId}`)
}

// Get all competitors for a specific service
export async function getCompetitorsForService(
  spaceId: string,
  serviceId: string,
  filterTarget: 'true' | 'false' | 'all' = 'all'
): Promise<PaginatedResponse<CompetitorResponse>> {
  // Use a simple object for params matching fetcher expectation
  const params: Record<string, string> = {}
  if (filterTarget !== 'all') {
    params.isMonitoringTarget = filterTarget // Use correct key based on API spec
  }
  // apiFetcher.get returns the data directly when typed
  const responseData = await apiFetcher.get<PaginatedResponse<CompetitorResponse>>(
    `/spaces/${spaceId}/services/${serviceId}/competitors`,
    // Pass the params object, ensure it's empty if no filter
    Object.keys(params).length > 0 ? { params } : undefined
  )
  return responseData // Return the data directly
}

export async function getServiceScores(
  spaceId: string,
  serviceId: string
): Promise<{
  priorityScore: number
  positiveScore: number
  negativeScore?: number
  charCountScore?: number
  domainCitationScore?: number
  lastUpdated: string
}> {
  return apiFetcher.get(`/spaces/${spaceId}/services/${serviceId}/scores`)
}

// --- CSV Import Operations ---

export async function importCompetitorsFromCSV(
  spaceId: string,
  serviceId: string,
  file: File
): Promise<{
  success: boolean
  message: string
  imported?: number
  errors?: Array<{ row: number; message: string }>
}> {
  try {
    const BASE_URL = import.meta.env.VITE_BACKEND_API_URL || 'http://localhost:8080/api'
    const url = `${BASE_URL}/spaces/${spaceId}/services/${serviceId}/competitors/import`

    // Get Firebase auth token
    const { auth } = await import('@/utils/firebaseConfig')
    const token = await auth.currentUser?.getIdToken()

    if (!token) {
      throw new Error('Authentication required')
    }

    // Get CSRF token for security
    const csrfToken = await csrfManager.getToken()

    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-CSRF-Token': csrfToken,
      },
      credentials: 'include',
      body: formData,
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.message || 'Import failed')
    }

    return data
  } catch (error) {
    console.error('CSV import error:', error)
    throw error
  }
}

export async function downloadCompetitorTemplate(
  spaceId: string,
  serviceId: string,
  language?: string
): Promise<void> {
  try {
    // Use fetch directly with proper authentication from apiFetcher
    const BASE_URL = import.meta.env.VITE_BACKEND_API_URL || 'http://localhost:8080/api'
    const params = new URLSearchParams()
    if (language) {
      params.set('lang', language)
    }
    const url = `${BASE_URL}/spaces/${spaceId}/services/${serviceId}/competitors/template${params.toString() ? `?${params.toString()}` : ''}`

    // Get Firebase auth token
    const { auth } = await import('@/utils/firebaseConfig')
    const token = await auth.currentUser?.getIdToken()

    if (!token) {
      throw new Error('Authentication required')
    }

    // Get CSRF token for consistency
    const csrfToken = await csrfManager.getToken()

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/csv',
        'X-CSRF-Token': csrfToken,
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Template download failed: ${response.status} ${errorText}`)
    }

    // Get the CSV content
    const blob = await response.blob()

    // Create a download link
    const downloadUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = 'competitor_template.csv'
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(downloadUrl)
    document.body.removeChild(a)
  } catch (error) {
    console.error('Template download error:', error)
    throw error
  }
}
